import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="direct-upload"
// Handles Active Storage direct upload events and displays individual file progress
export default class extends Controller {
  static targets = ["submitButton"]

  connect() {
    this.activeUploads = new Set()
    this.setupEventListeners()
  }

  disconnect() {
    this.removeEventListeners()
  }

  setupEventListeners() {
    this.handleInitialize = this.onDirectUploadInitialize.bind(this)
    this.handleStart      = this.onDirectUploadStart.bind(this)
    this.handleProgress   = this.onDirectUploadProgress.bind(this)
    this.handleError      = this.onDirectUploadError.bind(this)
    this.handleEnd        = this.onDirectUploadEnd.bind(this)

    this.element.addEventListener("direct-upload:initialize", this.handleInitialize)
    this.element.addEventListener("direct-upload:start",      this.handleStart)
    this.element.addEventListener("direct-upload:progress",   this.handleProgress)
    this.element.addEventListener("direct-upload:error",      this.handleError)
    this.element.addEventListener("direct-upload:end",        this.handleEnd)
  }

  removeEventListeners() {
    this.element.removeEventListener("direct-upload:initialize", this.handleInitialize)
    this.element.removeEventListener("direct-upload:start",      this.handleStart)
    this.element.removeEventListener("direct-upload:progress",   this.handleProgress)
    this.element.removeEventListener("direct-upload:error",      this.handleError)
    this.element.removeEventListener("direct-upload:end",        this.handleEnd)
  }

  get fileUploadContainer() {
    return this.element.closest('[data-controller*="file-upload"]')
  }

  findFileList() {
    const container = this.fileUploadContainer
    if (!container) return null
    return container.querySelector('[data-file-upload-target="fileList"]')
  }

  findFileItemByName(name) {
    const fileList = this.findFileList()
    if (!fileList) return null

    const fileItems = Array.from(fileList.querySelectorAll("[data-file-name]"))
    return fileItems.find((item) => item.dataset.fileName === name)
  }

  findFileItemByUploadId(id) {
    const container = this.fileUploadContainer || document
    return container.querySelector(`[data-upload-id="${id}"]`)
  }

  toggleRemoveButton(fileItem, disabled) {
    const removeButton = fileItem?.querySelector("button")
    if (!removeButton) return

    if (disabled) {
      removeButton.classList.add("hidden")
    } else {
      removeButton.classList.remove("hidden")
    }
  }

  updateStatusArea(fileItem, html) {
    const statusArea = fileItem?.querySelector(".upload-status-area")
    if (!statusArea) return
    statusArea.innerHTML = html
  }

  onDirectUploadInitialize(event) {
    const { id, file } = event.detail

    const fileItem = this.findFileItemByName(file.name)
    if (!fileItem) return

    // Store the upload ID on the file item
    fileItem.dataset.uploadId = id

    // Add a progress bar to the upload status area
    this.updateStatusArea(
      fileItem,
      `
        <div class="w-full bg-base-300 rounded-full h-1.5">
          <div id="direct-upload-progress-${id}" class="bg-primary h-1.5 rounded-full transition-all duration-300" style="width: 0%"></div>
        </div>
      `
    )

    // Disable the remove button during upload
    this.toggleRemoveButton(fileItem, true)
  }

  onDirectUploadStart(event) {
    const { id } = event.detail
    
    // Track active upload
    this.activeUploads.add(id)
    this.updateSubmitButton()
    
    const progressElement = document.getElementById(`direct-upload-progress-${id}`)
    if (progressElement) {
      progressElement.style.width = "1%"
    }
  }

  onDirectUploadProgress(event) {
    const { id, progress } = event.detail
    const progressElement = document.getElementById(`direct-upload-progress-${id}`)
    if (progressElement) {
      progressElement.style.width = `${progress}%`
    }
  }

  onDirectUploadError(event) {
    event.preventDefault()
    const { id, error } = event.detail

    // Remove from active uploads
    this.activeUploads.delete(id)
    this.updateSubmitButton()

    const fileItem = this.findFileItemByUploadId(id)
    if (!fileItem) return

    fileItem.classList.add("border-error")

    this.updateStatusArea(
      fileItem,
      `
        <div class="flex items-center gap-2 text-error">
          <svg class="size-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-xs">${error}</p>
        </div>
      `
    )

    // Keep button hidden even on error
  }

  onDirectUploadEnd(event) {
    const { id } = event.detail

    // Remove from active uploads
    this.activeUploads.delete(id)
    this.updateSubmitButton()

    const fileItem = this.findFileItemByUploadId(id)
    if (!fileItem) return

    fileItem.classList.add("border-success")

    this.updateStatusArea(
      fileItem,
      `
        <div class="flex items-center gap-2 text-success">
          <p class="text-xs font-medium">Uploaded successfully</p>
          <svg class="size-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
      `
    )

    // Keep button hidden after successful upload
  }

  updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return

    const isUploading = this.activeUploads.size > 0

    this.submitButtonTarget.disabled = isUploading
    
    if (isUploading) {
      this.submitButtonTarget.classList.add("btn-disabled", "opacity-50")
    } else {
      this.submitButtonTarget.classList.remove("btn-disabled", "opacity-50")
    }
  }
}